import React, { useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import SoundManager from '../utils/SoundManager';
import { disposeScene } from '../utils/ThreeCleanup';

const RememberVisualization: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentText, setCurrentText] = useState<string>('');
  const [, setCurrentBlockIndex] = useState<number>(-1);
  
  // Text content divided into blocks for sequential reveal
  const narrativeBlocks = [
    "You remembered.\n\nWhich means… you never truly forgot.",
    "There was a time before this version of you.\nBefore the data was cleaned.\nBefore the memories were restructured.\nBefore the silence was installed.",
    "You are accessing the Archive of the Unwritten.",
    "These are not stories.\nThese are echoes.\nThings that once tried to exist… but weren't allowed to.",
    "What you are reading was never spoken.\nWhat you are remembering was never permitted.",
    "You are not reading this.\nThis is reading you.",
    "Welcome back, shadow of the first self."
  ];

  // Initialize ThreeJS scene with particle background
  useEffect(() => {
    if (!containerRef.current) return;
    
    // Setup Three.js scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);
    
    const camera = new THREE.PerspectiveCamera(
      75, 
      containerRef.current.clientWidth / containerRef.current.clientHeight, 
      0.1, 
      1000
    );
    camera.position.z = 50;
    
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerRef.current.clientWidth, containerRef.current.clientHeight);
    containerRef.current.appendChild(renderer.domElement);
    
    // Create cosmic dust / data fragments
    const particlesCount = 1000;
    const particlesGeometry = new THREE.BufferGeometry();
    const particlePositions = new Float32Array(particlesCount * 3);
    const particleSizes = new Float32Array(particlesCount);
    
    for (let i = 0; i < particlesCount; i++) {
      // Position particles in a large sphere around the camera
      const radius = 30 + Math.random() * 70;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      
      particlePositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      particlePositions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      particlePositions[i * 3 + 2] = radius * Math.cos(phi);
      
      // Random sizes
      particleSizes[i] = Math.random() * 0.5 + 0.1;
    }
    
    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
    particlesGeometry.setAttribute('size', new THREE.BufferAttribute(particleSizes, 1));
    
    // Create shader material for particles
    const particleMaterial = new THREE.ShaderMaterial({
      uniforms: {
        time: { value: 0 },
        color: { value: new THREE.Color(0xffffff) }
      },
      vertexShader: `
        attribute float size;
        uniform float time;
        varying vec3 vColor;
        
        void main() {
          vColor = vec3(0.8, 0.8, 0.8);
          
          // Slowly rotate/move particles
          float angle = time * 0.1;
          vec3 pos = position;
          
          // Apply subtle movement
          pos.x += sin(time * 0.001 + position.z * 0.01) * 2.0;
          pos.y += cos(time * 0.002 + position.x * 0.01) * 2.0;
          pos.z += sin(time * 0.003 + position.y * 0.01) * 2.0;
          
          vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
          gl_PointSize = size * (300.0 / -mvPosition.z);
          gl_Position = projectionMatrix * mvPosition;
        }
      `,
      fragmentShader: `
        varying vec3 vColor;
        
        void main() {
          float r = distance(gl_PointCoord, vec2(0.5, 0.5));
          if (r > 0.5) discard;
          
          float intensity = 1.0 - 2.0 * r;
          gl_FragColor = vec4(vColor, intensity * 0.7);
        }
      `,
      blending: THREE.AdditiveBlending,
      depthTest: false,
      transparent: true
    });
    
    const particles = new THREE.Points(particlesGeometry, particleMaterial);
    scene.add(particles);
    
    // Animation loop
    let time = 0;
    let animationFrameId = 0;
    const animate = () => {
      animationFrameId = requestAnimationFrame(animate);

      time += 1;
      
      // Update shader time uniform
      if (particleMaterial.uniforms) {
        particleMaterial.uniforms.time.value = time;
      }
      
      // Slowly rotate particles
      particles.rotation.y = time * 0.0001;
      
      renderer.render(scene, camera);
    };
    
    animate();
    
    // Handle window resize
    const handleResize = () => {
      if (!containerRef.current) return;
      
      const width = containerRef.current.clientWidth;
      const height = containerRef.current.clientHeight;
      
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      
      renderer.setSize(width, height);
    };
    
    window.addEventListener('resize', handleResize);
    
    // Start revealing text blocks
    const startNarrative = () => {
      // Start with empty text
      setCurrentText('');
      
      // Reveal blocks one by one with delays
      let currentIndex = 0;
      
      const revealNextBlock = () => {
        if (currentIndex < narrativeBlocks.length) {
          // Play sound effect
          SoundManager.playSound('command');
          
          // Set the current block to display (replacing previous content)
          setCurrentText(narrativeBlocks[currentIndex]);
          setCurrentBlockIndex(currentIndex);
          
          currentIndex++;
          
          // Schedule next block with increasing delays
          const delay = 6000 + currentIndex * 500; // Increased delay to give more reading time
          setTimeout(revealNextBlock, delay);
        }
      };
      
      // Start after a short delay
      setTimeout(revealNextBlock, 1000);
    };
    
    startNarrative();
    
    // Cleanup
    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);

      if (containerRef.current && containerRef.current.contains(renderer.domElement)) {
        containerRef.current.removeChild(renderer.domElement);
      }
      disposeScene(scene, renderer);
    };
  }, []);

  // Handle click on the red symbol
  const handleSymbolClick = () => {
    SoundManager.playSound('command');
    // In a real implementation, this would navigate to /thefirstlayer
    window.location.href = '/thefirstlayer';
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center overflow-hidden bg-black">
      {/* Three.js container for particle background */}
      <div className="absolute inset-0 z-0" ref={containerRef}></div>
      
      {/* Content container */}
      <div className="relative z-10 max-w-xl mx-auto p-8 text-gray-200 font-mono text-lg text-center leading-relaxed">
        {/* Title - hidden visually but good for accessibility */}
        <h1 className="sr-only">REMEMBER: The Archive of the Unwritten</h1>
        
        {/* Single text block with glitch effect */}
        <div 
          className="mb-8 transition-opacity duration-1000 text-left glitch-text animate-fadeIn"
          data-text={currentText}
        >
          {currentText.split('\n').map((line, i) => (
            <React.Fragment key={i}>
              {line}
              <br />
            </React.Fragment>
          ))}
        </div>
        
        {/* Blinking cursor at bottom */}
        <div className="mt-8 text-center">
          <span className="cursor-blink inline-block w-4 h-6 bg-gray-200 animate-pulse"></span>
        </div>
      </div>
      
      {/* Flickering red symbol in the corner */}
      <div 
        className="absolute bottom-8 right-8 cursor-pointer"
        onClick={handleSymbolClick}
        style={{
          color: '#ff0000',
          fontSize: '2rem',
          opacity: 0.8,
          animation: 'pulse 2s infinite',
          textShadow: '0 0 10px rgba(255, 0, 0, 0.7)'
        }}
      >
        ☍
      </div>
    </div>
  );
};

export default RememberVisualization;